// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { SalesRepModel } from "@store/Framework/Types/ApiModels";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC } from "react";
import { css } from "styled-components";
import Icon, { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import EmailLine from "@store/Library/Components/Toolkit/Icons/Email/EmailLine";
import PhoneLine from "@store/Library/Components/Toolkit/Icons/Phone/PhoneLine";

export interface SalesPersonInfosPresentationProps extends ExpandStyles<SalesPersonInfosStyles> {}

interface OwnProps extends SalesPersonInfosPresentationProps {
    session: Session;
    showIcons?: boolean;
}

interface SalesPersonInfosStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyProps;
    nameText?: TypographyProps;
    infoWrapper?: InjectableCss;
    infoText?: TypographyProps;
    emailIcon?: IconPresentationProps;
    phoneIcon?: IconPresentationProps;
}

const salesPersonInfosStylesStyles: SalesPersonInfosStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(48), pxToRem(32))}
            display: flex;
            flex-direction: column;
        `,
    },
    headerText: {
        as: "span",
        css: css`
            display: inline-block;
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-bottom: ${pxToRem(6)};
        `,
    },
    nameText: {
        as: "p",
    },
    infoText: {
        as: "p",
    },
    emailIcon: {
        src: EmailLine,
        size: 18,
        css: css`
            margin-right: ${pxToRem(4)};
        `,
    },
    phoneIcon: {
        src: PhoneLine,
        size: 18,
        css: css`
            margin-right: ${pxToRem(4)};
        `,
    },
};

interface SalesPersonJsonModel {
    salesperson: SalesRepModel;
    telOffice: string;
}

const SalesPersonInfos: FC<OwnProps> = ({ session, showIcons, extendedStyles, overridedStyles }) => {
    const styles = useExpandProps(salesPersonInfosStylesStyles, extendedStyles, overridedStyles);
    const { salesperson } = session.properties;

    if (!salesperson) {
        return null;
    }

    const salesRepJson: SalesPersonJsonModel = JSON.parse(salesperson);
    const { salesperson: salesRep, telOffice } = salesRepJson;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="salesPersonInfo">
            <Typography {...styles.headerText}>{translate("Account Manager")}</Typography>
            <StyledWrapper {...styles.infoWrapper}>
                {salesRep.name && (
                    <Typography {...styles.nameText}>
                        <Typography>
                            {salesRep.name}
                            {salesRep.title && ` | ${salesRep.title}`}
                        </Typography>
                    </Typography>
                )}
                {salesRep.email && (
                    <Typography {...styles.infoText}>
                        {showIcons ? (
                            <Icon {...styles.emailIcon} title={translate("Email")} />
                        ) : (
                            <Typography>{`${translate("{0}:", translate("Email"))} `} </Typography>
                        )}

                        <Link href={`mailto:${salesRep.email}`}>{salesRep.email}</Link>
                    </Typography>
                )}
                {telOffice && (
                    <Typography {...styles.infoText}>
                        {showIcons ? (
                            <Icon {...styles.phoneIcon} title={translate("Office")} />
                        ) : (
                            <Typography>{`${translate("{0}:", translate("Office"))} `} </Typography>
                        )}
                        <Typography>{telOffice}</Typography>
                    </Typography>
                )}
                {salesRep.phone1 && (
                    <Typography {...styles.infoText}>
                        {showIcons ? (
                            <Icon {...styles.phoneIcon} title={translate("Phone")} />
                        ) : (
                            <Typography>{`${translate("{0}:", translate("Phone"))} `} </Typography>
                        )}
                        <Typography>{salesRep.phone1}</Typography>
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default SalesPersonInfos;
